import React, {useEffect, useRef, useState} from 'react';
import { EnergySavingsLeaf, Visibility, VisibilityOff, Code } from '@mui/icons-material';
import { ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, InputAdornment, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import useAxiosProtect from '../../hooks/useAxiosProtect';
import useAuth from '../../hooks/useAuth';
import jwtDecode from 'jwt-decode';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Switch from '@mui/material/Switch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import APIWarningModal from './APIWarningModal';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ProfilePage = props => {
    const [activePage, setActivePage] = useState("General");
    const [containerHeight, setContainerHeight] = useState(0);
    const [showPassChangeModal, setShowPassChangeModal] = useState(false);
    const axiosProtect = useAxiosProtect();
    const {auth, persist} = useAuth();

    const [uid, setUID] = useState('');
    const [email, setEmail] = useState('');

    const [passwordStates, setPasswordStates] = useState({
        passEditOn: false,
        current: {
            value: '',
            show: false,
            error: ''
        },
        new: {
            value: '',
            show: false,
            error: ''
        }
    });

    const [open, setOpen] = useState(false);

    const showToast = () => {
        setAPIErrorMessage('');
        setOpen(true);
    };

    const closeToast = (event, reason) => {
        if (reason === 'clickaway') {
        return;
        }

        setOpen(false);
    };

    const handleClickShowPassword = field => {
        setPasswordStates(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                show: !prev[field].show
            }
        }));
    }

    const changePage = page => {
        if(activePage === page) return;
        setActivePage(page);
    }

    const updatePassValue = (value, field) => {
        setPasswordStates(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                value: value
            }
        }));
    }

    const updatePassFormVisibility = show => {
        setPasswordStates(prev => ({
            ...prev,
            passEditOn: show
        }));
    }

    const requestPassChange = async () => {
        try{
            let response = await axiosProtect.post('/auth/pass/change', {
                uid,
                old_pass: passwordStates.current.value,
                new_pass: passwordStates.new.value,
            });

            return response.data;
        }catch(error){
            return error.response;
        }
    }

    const validatePassChange = async () => {
        let currError = '';
        let newError = '';
        let isValid = true;

        if(passwordStates.current.value === ''){
            currError = 'Required';
            isValid = false;
        }else currError = '';

        if(passwordStates.new.value === ''){
            newError = 'Required';
            isValid= false;
        }else newError = '';

        if(!isValid){
            setPasswordStates(prev => ({
                ...prev,
                current: {
                    ...prev.current,
                    error: currError
                },
                new: {
                    ...prev.new,
                    error: newError
                },
            }));

            return isValid;
        }

        let response = await requestPassChange();

        if(response === 'Password changed'){
            clearPassForm();
            return true;

        }else if(response.data === 'Invalid password'){
            newError = 'Length must be >= 8, contain >= 1 number, >= 1 letter, >= 1 special character (@$!%*#?&)';
            currError = '';
        }else if(response.data === 'Incorrect password'){
            currError = response.data;
            newError = '';
        }else{
            newError = response.status === 400 ? response.data : "Something went wrong";
            currError = '';
        }

        setPasswordStates(prev => ({
            ...prev,
            current: {
                ...prev.current,
                error: currError
            },
            new: {
                ...prev.new,
                error: newError
            },
        }));

        return false;
    }

    const clearPassForm = () => {
        setPasswordStates({
            passEditOn: false,
            current: {
                value: '',
                show: false,
                error: ''
            },
            new: {
                value: '',
                show: false,
                error: ''
            }
        });
    }

    const submitPassChange = async () => {
        let isValidPassChange = await validatePassChange();

        if(isValidPassChange) showToast();
    }

    // api page
    const [apiAccess, setAPIAccess] = useState(false);
    const [apiKeyState, setAPIKeyState] = useState({
        value: '',
        visible: false // empty if no key, hidden if pre-existing key, visible if generated key
    });
    const [apiErrorMessage, setAPIErrorMessage] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [clientIdIsCopied, setClientIdIsCopied] = useState(false);
    const [apiModalState, setAPIModalState] = useState('');

    const showAPIToast = message => {
        setOpen(false);
        setAPIErrorMessage(message);
    }

    const closeAPIToast = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setAPIErrorMessage('');
    }

    const toggleAPIStatus = (triggeredByModal=false) => {
        if(triggeredByModal || !apiAccess){
            axiosProtect.post('/api/data/toggle-api', {
                client_uid: auth?.id,
                org_id: auth?.org_id
            }).then(response => {
                setAPIAccess(response.data.apiEnabled);

                if(response.data.apiEnabled){
                    setAPIKeyState({
                        value: '',
                        visible: false
                    });
                }
            }).catch(error => {
                showAPIToast(error.response.data.error);
            });

            setAPIModalState('');
        }else{
            setAPIModalState('disable');
        }
    }

    const updateAPIKey = (triggeredByModal=false) => {
        if(triggeredByModal || apiKeyState.value === ''){
            axiosProtect.post('/api/data/create-key', {
                client_uid: auth?.id,
                org_id: auth?.org_id
            }).then(response => {
                setAPIKeyState(prev => ({
                    ...prev,
                    value: response.data.apiKey,
                    visible: true
                }));
            }).catch(error => {
                // console.log(error.response);
            });

            setAPIModalState('');
        }else{
            setAPIModalState('genKey');
        }
    }

    const copyAPIKey = () => {
        navigator.clipboard.writeText(apiKeyState.value);
        setIsCopied(true);

        setTimeout(() => {
            setIsCopied(false);
        }, 1500);
    }

    const copyClientId = () => {
        navigator.clipboard.writeText(auth?.org_id);
        setClientIdIsCopied(true);

        setTimeout(() => {
            setClientIdIsCopied(false);
        }, 1500);
    }

    const closeAPIModal = () => {
        setAPIModalState('');
    }

    var init = useRef(true);

    useEffect(() => {
        const handleResize = () => {
            try{
                setContainerHeight(window.innerHeight - document.getElementsByClassName('navbar navbar-expand-lg navbar-light sticky-top')[0].offsetHeight);
            }catch(err){
                setContainerHeight(-1);
            }
        }

        if(init.current){
            init.current = false;
            handleResize();

            if(auth?.accessToken){
                let tokenObj = jwtDecode(auth.accessToken);
                setUID(tokenObj.id);
                setEmail(tokenObj.email);

                axiosProtect.get(`/api/data/api-status?org_id=${auth.org_id}&client_uid=${auth.id}`).then(response => {
                    setAPIAccess(response.data.apiEnabled);
                    setAPIKeyState(prev => ({
                        ...prev,
                        value: response.data.hasKey ? 'api-placeholder' : ''
                    }))
                }).catch(error => {
                    console.log(error);
                });
            }
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize)
        }

    }, []);

    return(
        <div style={{ width:'100%', display:'flex', flexDirection:'row', height:`${containerHeight < 0 ? 'fit-content' : `${containerHeight}px`}`,overflowY:'hidden'}}>
            {apiModalState !== '' && <APIWarningModal apiModalState={apiModalState} closeAPIModal={closeAPIModal} updateAPIKey={updateAPIKey} toggleAPIStatus={toggleAPIStatus}/>}
            <div style={{height:'100%',  flex:'1'}}>

                <br/>

                <h4 style={{padding:'0 5%'}}>Account settings</h4>

                <ListItem disablePadding={activePage !== 'General'}>
                    <ListItemButton selected={activePage === 'General'} onClick={() => changePage('General')}>
                        <ListItemIcon>
                            <EnergySavingsLeaf className='text-brand'/>
                        </ListItemIcon>
                        <ListItemText primary="General" className='text-muted-dark'/>
                    </ListItemButton>
                </ListItem>

                {
                    (auth?.org_id === '036a49ea-e21a-40ce-b136-3f91fbe9d5bf' && parseInt(auth?.role) >= parseInt(process.env.REACT_APP_CLIENT_ADMIN)) && 
                    <ListItem disablePadding={activePage !== 'API'}>
                        <ListItemButton selected={activePage === 'API'} onClick={() => changePage('API')}>
                            <ListItemIcon>
                                <Code className='text-brand'/>
                            </ListItemIcon>
                            <ListItemText primary="API" className='text-muted-dark'/>
                        </ListItemButton>
                    </ListItem>
                }
            </div>

            <div style={{height:'100%', padding:'2%', backgroundColor:'#f5f5f5', flex:'3.8', overflowY:'auto'}}>
                {
                    activePage === 'API' ?
                    <div>
                        <FormGroup style={{width:'fit-content'}}>
                            <FormControlLabel control={<Switch checked={apiAccess} onChange={() => toggleAPIStatus()}/>} label={`API access is ${apiAccess ? 'enabled' : 'disabled'}`} />
                        </FormGroup>
                        
                        <br/>

                        <p>Once API access is enabled, please follow the documentation <a style={{color:'rgb(8,114,182)', textDecoration:'underline', cursor:'pointer'}} href="https://docs.stewwi.com" target="_blank" rel="noopener noreferrer">here</a>. Note that disabling API access or regenerating the existing key on this page or at any point will reset the API key if one has been generated.</p>

                        <p>When generating a key, make sure to <strong>securely store it immediately.</strong> Once you leave this page, it will be permanently redacted and you must generate a new key if you lose reference to the existing one.</p>

                        {
                            apiAccess &&
                            <>
                            <p>
                                Your <code style={{fontWeight:'bold', color:'black', backgroundColor:'lightgrey', padding:'5px'}}>
                                    client_id
                                    </code> value is <strong style={{color:'rgb(8,114,182)', marginRight:'10px'}}>
                                        {auth?.org_id}
                                        </strong> 
                                        {clientIdIsCopied ? 
                                            <CheckIcon/> : 
                                            <ContentCopyIcon style={{cursor:'pointer'}} onClick={e => copyClientId()}/>
                                        }
                            </p>
                            <br/>
                            <OutlinedInput
                                readOnly
                                fullWidth
                                variant="filled"
                                inputProps={{
                                    style: {
                                        userSelect: "none",
                                        WebkitUserSelect: "none",
                                        MozUserSelect: "none",
                                        msUserSelect: "none",
                                        caretColor: "transparent",
                                    },
                                    onMouseDown: e => e.preventDefault(),
                                    onKeyDown: e => {
                                        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
                                            e.preventDefault();
                                        }
                                    }
                                }}
                                placeholder='Your API key'
                                value={apiKeyState.value}
                                type={apiKeyState.visible ? 'text' : 'password'}
                                endAdornment={
                                    (apiKeyState.value !== '' && apiKeyState.visible) ?
                                    <InputAdornment position="end">
                                        <IconButton style={{fontSize:'1rem'}} onClick={copyAPIKey} edge="end">
                                            {isCopied ? <CheckIcon/> : <ContentCopyIcon/>}
                                        </IconButton>
                                    </InputAdornment>
                                    :
                                    <></>
                                }
                            />
                            <br/>
                            <Button onClick={() => updateAPIKey()}>Generate key</Button>
                            </>
                        }
                    </div>

                    :

                    <>
                    <div style={{width:'100%', backgroundColor:'white', height:'fit-content', borderRadius:'1%', boxShadow:'2px 6px 12px rgb(164, 164, 164)', padding:'1%'}}>
                        <h4>Account information</h4>
                        <p className='m-0'>{`${email}`}</p>
                    </div>

                    <br/>
                    
                    <Button className='btn-brand-bg' variant="contained" onClick={() => updatePassFormVisibility(true)}>Change password</Button>

                    {passwordStates.passEditOn && <div style={{width:'33%', backgroundColor:'white', height:'fit-content', borderRadius:'1%', boxShadow:'2px 6px 12px rgb(164, 164, 164)', padding:'1.5%', marginTop:'2%'}}>
                        <TextField
                            label="Current password"
                            error={passwordStates.current.error !== ''}
                            helperText={passwordStates.current.error}
                            value={passwordStates.current.value}
                            onChange={e => updatePassValue(e.target.value, 'current')}
                            className='w-full'
                            type={passwordStates.current.show ? 'text' : 'password'}
                            InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => handleClickShowPassword('current')}
                                    >
                                    {passwordStates.current.show ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            }}
                            variant="outlined"
                        />

                        <br/><br/>

                        <TextField
                            label="New password"
                            error={passwordStates.new.error !== ''}
                            helperText={passwordStates.new.error}
                            value={passwordStates.new.value}
                            onChange={e => updatePassValue(e.target.value, 'new')}
                            className='w-full'
                            type={passwordStates.new.show ? 'text' : 'password'}
                            InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => handleClickShowPassword('new')}
                                    >
                                    {passwordStates.new.show ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            }}
                            variant="outlined"
                        />

                        <br/>
                        <br/>

                        <div className='field-row'>
                            <Button style={{flex:'8'}} variant="text" onClick={() => clearPassForm()}>Cancel</Button>
                            <div style={{flex:'3'}}></div>
                            <Button className='btn-brand-bg' style={{flex:'10'}} variant="contained" onClick={() => submitPassChange()}>Confirm</Button>
                        </div>

                        
                    </div>}
                    </>
                }

                <Stack spacing={2} sx={{ width: '100%' }}>
                    <Snackbar anchorOrigin={{ vertical:'bottom', horizontal:'right' }} open={apiErrorMessage !== ''} autoHideDuration={6000} onClose={closeAPIToast}>
                        <Alert onClose={closeAPIToast} severity="error" sx={{ width: '100%' }}>
                            {apiErrorMessage}
                        </Alert>
                    </Snackbar>

                    <Snackbar anchorOrigin={{ vertical:'bottom', horizontal:'right' }} open={open} autoHideDuration={6000} onClose={closeToast}>
                        <Alert onClose={closeToast} severity="success" sx={{ width: '100%' }}>
                            Password changed
                        </Alert>
                    </Snackbar>
                </Stack>
            </div>
        </div>
    )
}

export default ProfilePage;